// The fairness read-out (TRADES.md "The builder", step 4): three explained
// lines from our own projections. Never a grade. From the point of view of
// `mine`: what I give, what I get, what my lineup and depth do after.
//
// SPOKEN TO `mine`, or ABOUT it (`named`): a manager in the trade reads
// "You give"; anybody else reading somebody else's trade reads the team's
// name, since a "you" there is nobody they are (B-389).

import type { TradeReadout, ValueContext } from './types';

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

function over(ctx: ValueContext, ids: readonly string[], picks: readonly string[]): number {
  return (
    ids.reduce(
      (sum, id) => sum + Math.max(0, ctx.value(id) - ctx.replacement(ctx.positionOf(id))),
      0,
    ) + picks.reduce((sum, id) => sum + (ctx.pick?.(id).over ?? 0), 0)
  );
}

/** A side as the read-out weighs it: its roster, what it gives, its drops, and the draft picks it gives (§2.6). */
interface ReadoutSide {
  teamId: string;
  roster: readonly string[];
  give: readonly string[];
  drops: readonly string[];
  givePicks?: readonly string[];
}

export function readout(
  input: { mine: ReadoutSide; theirs: ReadoutSide },
  ctx: ValueContext,
  /** Say `mine` by its name rather than "you": for a reader who is not in the trade. */
  named = false,
): TradeReadout {
  const { mine, theirs } = input;
  const myPicks = mine.givePicks ?? [];
  const theirPicks = theirs.givePicks ?? [];
  const giveValue = round1(over(ctx, mine.give, myPicks));
  const getValue = round1(over(ctx, theirs.give, theirPicks));
  // EACH SIDE'S WORTH, SUMMED (DIRECTION A19): what the first sentence says.
  // It said the points over the best free agent, which read zero for most of
  // a bench in a league whose wire is deep (the trade review of 2026-10-22);
  // that figure stays in `value`, for the bots and the lopsided line, and the
  // free agent is named in a sentence of his own below.
  // A DRAFT PICK COUNTS what the player at its place is worth now (§2.6
  // rule 7), beside the players.
  const pickWorth = (id: string) => ctx.pick?.(id).worth ?? 0;
  const plain = (ids: readonly string[], picks: readonly string[]) =>
    round1(
      ids.reduce((sum, id) => sum + ctx.value(id), 0) +
        picks.reduce((sum, id) => sum + pickWorth(id), 0),
    );
  const plainGive = plain(mine.give, myPicks);
  const plainGet = plain(theirs.give, theirPicks);
  const picks = [...myPicks, ...theirPicks];
  // A position with no free agent to weigh has a replacement level of
  // nothing, and nobody is matched by nobody.
  const matched = [...mine.give, ...theirs.give].filter((id) => {
    const wire = ctx.replacement(ctx.positionOf(id));
    return wire > 0 && ctx.value(id) <= wire;
  });
  // OUT, WITH A WORTH (B-185): the report cannot say for how long, so his
  // worth counts him back after a stated stretch (`outWeeks`), and a manager
  // is told rather than left to read his number as a promise.
  const out = [...mine.give, ...theirs.give].flatMap((id) => {
    const status = ctx.outStatus?.(id) ?? null;
    return status !== null && ctx.value(id) > 0
      ? [{ id, status, weeks: ctx.outWeeks?.(id) ?? null }]
      : [];
  });
  const backAfter = (weeks: number | null) =>
    weeks === 1 ? 'this week' : weeks === null || weeks === 2 ? 'next week' : `${weeks} weeks`;
  const after = (side: ReadoutSide, incoming: readonly string[]) => [
    ...side.roster.filter((id) => !side.give.includes(id) && !side.drops.includes(id)),
    ...incoming,
  ];
  const mineAfter = after(mine, theirs.give);
  const theirsAfter = after(theirs, mine.give);
  const lineup = {
    mine: { before: round1(ctx.bestLineup(mine.roster)), after: round1(ctx.bestLineup(mineAfter)) },
    theirs: {
      before: round1(ctx.bestLineup(theirs.roster)),
      after: round1(ctx.bestLineup(theirsAfter)),
    },
  };

  const possessive = (name: string) => (name.endsWith('s') ? `${name}'` : `${name}'s`);
  const myName = ctx.teamName(mine.teamId);
  const you = named ? myName : 'You';
  const your = named ? possessive(myName) : 'your';
  const youd = named ? `${myName} would` : "You'd";

  // Depth: positions where my count crosses a line that matters.
  const depth: string[] = [];
  const positions = new Set([...mine.roster, ...theirs.give].map(ctx.positionOf));
  for (const pos of positions) {
    const before = mine.roster.filter((id) => ctx.positionOf(id) === pos).length;
    const now = mineAfter.filter((id) => ctx.positionOf(id) === pos).length;
    const need = ctx.starterNeed(pos);
    if (need === 0 || before === now) continue;
    if (now < need) depth.push(`${youd} be short a ${pos} for ${your} starting lineup.`);
    else if (before < need && now >= need) depth.push(`Fills ${your} empty ${pos} slot.`);
    else if (now === need && before > need)
      depth.push(
        `${youd} be down to ${need === 1 ? 'one' : need} ${pos}${need === 1 ? '' : 's'}, with nobody behind.`,
      );
    else if (now > before && before <= need) depth.push(`Adds depth at ${pos}.`);
  }

  const theirPossessive = possessive(ctx.teamName(theirs.teamId));
  const swing = round1(lineup.mine.after - lineup.mine.before);
  const theirSwing = round1(lineup.theirs.after - lineup.theirs.before);
  // "gains 4.2 points a week", "loses 1.1", "stays the same".
  const moves = (n: number, unit: string) =>
    n > 0 ? `gains ${n}${unit}` : n < 0 ? `loses ${Math.abs(n)}${unit}` : 'stays the same';
  const listed = (names: readonly string[]) =>
    names.length <= 1
      ? (names[0] ?? '')
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]!}`;
  const sentences = [
    `${you} ${named ? 'gives' : 'give'} ${plainGive} points a week and ${named ? 'gets' : 'get'} ${plainGet}.`,
    `${named ? your : 'Your'} best lineup ${moves(swing, ' points a week')}; ${theirPossessive} ${moves(theirSwing, '')}.`,
    // THE FREE AGENT, NAMED rather than subtracted (A19): a player the wire
    // can match is worth no more than a pickup to a manager with the room.
    ...(matched.length
      ? [
          `${matched.length === 1 ? 'A free agent scores' : 'Free agents score'} as much as ${listed(matched.map(ctx.playerName))}.`,
        ]
      : []),
    // EACH PICK'S NUMBER, said as a player's is: what it counts and why.
    ...(picks.length
      ? [
          `${picks.length === 1 ? 'A pick counts' : 'Each pick counts'} what the player at its place in its draft is worth now: ${picks
            .map((id) => `${ctx.pickName?.(id) ?? 'a draft pick'} ${round1(pickWorth(id))}`)
            .join(', ')}.`,
        ]
      : []),
    ...out.map(
      (o) =>
        `${ctx.playerName(o.id)} is listed ${o.status}: his worth counts him back after ${backAfter(o.weeks)}.`,
    ),
    ...(depth.length ? [depth.join(' ')] : []),
  ];
  return {
    value: { give: giveValue, get: getValue },
    plain: { give: plainGive, get: plainGet },
    lineup,
    depth,
    sentences,
  };
}
